import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Params, useNavigate, useParams } from 'react-router-dom'
import { getActiveProfile, getUserLanguageValue, onBackKeyPress, onEnterKeyPressAndClick } from '@utils/helpers'
import { ProfileTitle } from '@components/Profile/ProfileTitle/ProfileTitle'
import { ProfileButton } from '../../../components/Profile/ProfileButton/ProfileButton'
import {
  getDeleteProfileIdRoute,
  getEditLangProfileIdRoute,
  getEditNameProfileIdRoute,
  getEditProfileIdPasswordRoute,
  getEditChooseIconRoute,
  routes,
} from '@constants/routes.const'
import { language } from '@constants/lang.const'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import { VelvetSDK } from 'sdk'
import './EditProfile.scss'
import classNames from 'classnames'
import { useUserProfile } from '@hooks/useUserProfile'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import SpatialNavigation from '@utils/spatial-navigation.js'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { useTypedSelector } from 'store'
import {
  setParentalAction,
  setParentalControl,
  setParentalStateSnapshot,
  setProfileContentClassification,
  setProfileLang,
  setProfileName,
  setProfilePhoto,
  setProfilePIN,
} from '@actions/profileActions'
import { ParentalControlStep } from '@enums/profile.enum'
import { ProfileIcon } from '@components/Profile/ProfileIcon/ProfileIcon'
import { UpdateUserProfileParams } from 'sdk/services/storage/profiles/types/profilesManager'
import { useUserProfiles } from '@hooks/useUserProfiles'
import { fetchMenu } from '@actions/menuActions'
import { LocalStorageItem } from '@constants/index'

interface EditProfileProps {
  showMenu: (status: boolean) => void
}

export const EditProfile = ({ showMenu }: EditProfileProps) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { profileId } = useParams<Params>()
  const navigateToManageProfiles = routes.MANAGE_PROFILES
  const V = VelvetSDK.Vocabulary.vocabulary
  const SN = SpatialNavigation
  const { profileData, isLoading } = useUserProfile({
    profileId: profileId || '',
  })
  const parentalControlStorage = profileData?.parentalControl?.contentClassification
  const nameRef = useRef<HTMLDivElement>(null)
  const contentClassificationLocal = useTypedSelector((state) => state.profile.contentClassification)
  const profilePINLocal = useTypedSelector((state) => state.profile.pin)
  const parentalControlLocal = useTypedSelector((state) => state.profile.parentalControl)
  const profileNameLocal = useTypedSelector((state) => state.profile.name)
  const profilePhotoLocal = useTypedSelector((state) => state.profile.photo)
  const profileLangLocal = useTypedSelector((state) => state.profile.lang)
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false)
  const userObject = JSON.parse(localStorage.getItem(LocalStorageItem.USER_DATA) as string)
  const activeProfile = getActiveProfile()

  const isParentalControlEnabled = !!(
    profileData?.parentalControl?.contentClassification && profileData?.parentalControl?.parentalPIN
  )
  const backToManageProfilePage = useCallback(
    (e: KeyboardEvent) =>
      onBackKeyPress(e, () => {
        dispatch(setParentalControl(null))
        dispatch(setProfileContentClassification(''))
        dispatch(setProfilePIN(''))
        dispatch(setProfileName(''))
        dispatch(setProfilePhoto(null))
        navigate(routes.MANAGE_PROFILES)
      }),
    [dispatch, navigate],
  )

  const handleNavigation = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent, route: string) => {
      e.preventDefault()
      onEnterKeyPressAndClick(e, () => navigate(route))
    },
    [navigate],
  )

  const handleCancel = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      e.preventDefault()
      onEnterKeyPressAndClick(e, () => {
        dispatch(setParentalControl(null))
        dispatch(setProfileContentClassification(''))
        dispatch(setProfilePIN(''))
        dispatch(setProfileName(''))
        dispatch(setProfilePhoto(null))
        handleNavigation(e, navigateToManageProfiles)
      })
    },
    [dispatch, handleNavigation, navigateToManageProfiles],
  )

  const handleContentClassification = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      e.preventDefault()
      onEnterKeyPressAndClick(e, () => {
        // Always set the action "change" if we have values (local OR Firestore)
        if (profilePINLocal || contentClassificationLocal || parentalControlStorage) {
          dispatch(setParentalAction(ParentalControlStep.CHANGE_CONTENT))
        }

        handleNavigation(e, getEditProfileIdPasswordRoute(profileId))
      })
    },
    [contentClassificationLocal, dispatch, handleNavigation, parentalControlStorage, profileId, profilePINLocal],
  )

  const handlePinSetup = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      e.preventDefault()
      onEnterKeyPressAndClick(e, () => {
        // Always set the action "change" if we have values (local OR Firestore)
        if (profilePINLocal || contentClassificationLocal || parentalControlStorage) {
          dispatch(setParentalAction(ParentalControlStep.CHANGE_PIN))
        }

        handleNavigation(e, getEditProfileIdPasswordRoute(profileId))
      })
    },
    [contentClassificationLocal, dispatch, handleNavigation, parentalControlStorage, profileId, profilePINLocal],
  )

  const parentalControlBtnValue = useCallback(() => {
    // If user has explicitly changed state locally (true or false), use that
    if (parentalControlLocal === true) {
      return V.parentalControlOn
    }

    if (parentalControlLocal === false) {
      return V.parentalControlOff
    }

    // If no local changes (parentalControlLocal is null), check Firestore
    if (parentalControlLocal === null) {
      if (parentalControlStorage) {
        return V.parentalControlOn
      }

      // Check if we have unsaved local values (user went through flow but didn't save)
      if (contentClassificationLocal && profilePINLocal) {
        return V.parentalControlOn
      }
    }

    // Default: OFF
    return V.parentalControlOff
  }, [
    V.parentalControlOff,
    V.parentalControlOn,
    contentClassificationLocal,
    parentalControlLocal,
    parentalControlStorage,
    profilePINLocal,
  ])

  const contentClassBtnValue = () => {
    if (parentalControlLocal === false) {
      return ''
    }

    // It should show local value first, then Firestore
    const valueToShow = contentClassificationLocal || parentalControlStorage

    if (valueToShow) {
      return `${valueToShow} ${V.restrictionsByAge}`
    }

    return ''
  }

  const pinBtnValue = () => {
    if (parentalControlLocal === false) {
      return ''
    }

    // It should show local value first, then Firestore
    if (profilePINLocal || parentalControlStorage) {
      return V.parentalControlChange
    }

    return ''
  }

  const handleParentalControl = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      e.preventDefault()
      onEnterKeyPressAndClick(e, () => {
        // Determine effective state - prioritize local changes
        let effectiveParentalControl
        if (parentalControlLocal !== null) {
          // User has made local changes
          effectiveParentalControl = parentalControlLocal
        } else {
          // No local changes, use Firestore
          effectiveParentalControl = parentalControlStorage ? true : false
        }

        const effectiveContentClass = contentClassificationLocal || parentalControlStorage || ''
        const effectivePin =
          profilePINLocal || (parentalControlStorage ? profileData?.parentalControl?.parentalPIN : '') || ''

        // SAVE SNAPSHOT
        dispatch(
          setParentalStateSnapshot({
            parentalControl: effectiveParentalControl,
            contentClassification: effectiveContentClass,
            pin: effectivePin,
          }),
        )

        // Determine action based on what the button SHOWS (not Firestore)
        const buttonShowsOn = parentalControlBtnValue() === V.parentalControlOn

        if (buttonShowsOn) {
          dispatch(setParentalAction(ParentalControlStep.SET_PARENTAL_OFF))
        } else {
          dispatch(setParentalAction(ParentalControlStep.SET_PARENTAL_ON))
        }

        handleNavigation(e, getEditProfileIdPasswordRoute(profileId))
      })
    },
    [
      V.parentalControlOn,
      contentClassificationLocal,
      dispatch,
      handleNavigation,
      parentalControlLocal,
      parentalControlBtnValue,
      parentalControlStorage,
      profileData?.parentalControl?.parentalPIN,
      profileId,
      profilePINLocal,
    ],
  )

  const hasChanges = useCallback(() => {
    if (!profileData) return false

    if (profileNameLocal && profileNameLocal !== profileData?.name) return true
    if (profileLangLocal && profileLangLocal !== profileData?.preferredLanguage) return true

    if (profilePhotoLocal) {
      const type = profileData?.image?.type
      const backgroundColor = profileData?.image?.backgroundColor
      const color = profileData?.image?.color
      const slug = profileData?.image?.slug
      const url = profileData?.image?.url

      if (profilePhotoLocal.type !== type) return true
      if (profilePhotoLocal.backgroundColor !== backgroundColor) return true
      if (profilePhotoLocal.color !== color) return true
      if (profilePhotoLocal.slug !== slug) return true
      if (profilePhotoLocal.url !== url) return true
    }

    if (parentalControlLocal === false && isParentalControlEnabled) return true

    if (parentalControlLocal === true || contentClassificationLocal || profilePINLocal) {
      const currentClassification = profileData?.parentalControl?.contentClassification || ''
      const currentPin = profileData?.parentalControl?.parentalPIN || ''

      if (contentClassificationLocal && contentClassificationLocal !== currentClassification) return true
      if (profilePINLocal && profilePINLocal !== currentPin) return true
    }

    return false
  }, [
    contentClassificationLocal,
    isParentalControlEnabled,
    parentalControlLocal,
    profileData,
    profileNameLocal,
    profilePINLocal,
    profilePhotoLocal,
    profileLangLocal,
  ])

  const fetchVocabulary = useCallback(async (language: string) => {
    await VelvetSDK.Vocabulary.service.fetchVocabulary(language)
  }, [])

  const handleDone = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      e.preventDefault()
      onEnterKeyPressAndClick(e, async () => {
        setIsLoadingUpdate(true)

        const updatedProfile: UpdateUserProfileParams = {
          profileId: profileId ?? '',
        }

        if (profileNameLocal && profileNameLocal !== profileData?.name) {
          updatedProfile.name = profileNameLocal
        }

        if (profileLangLocal && profileLangLocal !== profileData?.preferredLanguage) {
          updatedProfile.lang = profileLangLocal
        }

        if (profilePhotoLocal) {
          const type = profileData?.image?.type
          const backgroundColor = profileData?.image?.backgroundColor
          const color = profileData?.image?.color
          const slug = profileData?.image?.slug
          const url = profileData?.image?.url

          // Check if any property has changed
          if (
            profilePhotoLocal.type !== type ||
            profilePhotoLocal.backgroundColor !== backgroundColor ||
            profilePhotoLocal.color !== color ||
            profilePhotoLocal.slug !== slug ||
            profilePhotoLocal.url !== url
          ) {
            updatedProfile.image = profilePhotoLocal
          }
        }

        // Check if user explicitly turned OFF parental control
        if (parentalControlLocal === false) {
          // User turned it OFF - clear everything in Firestore
          const hasExistingParentalControl = !!(
            profileData?.parentalControl?.contentClassification || profileData?.parentalControl?.parentalPIN
          )

          if (hasExistingParentalControl) {
            if (!updatedProfile.parentalControl) {
              updatedProfile.parentalControl = {}
            }
            updatedProfile.parentalControl.contentClassification = null
            updatedProfile.parentalControl.parentalPIN = null
          }
        }

        // Check if parental control is ON (either in Firestore or local)
        else if (isParentalControlEnabled || contentClassificationLocal || profilePINLocal) {
          const currentClassification = profileData?.parentalControl?.contentClassification || ''
          const currentPin = profileData?.parentalControl?.parentalPIN || ''

          // Only update if value exists and is different
          if (contentClassificationLocal && contentClassificationLocal !== currentClassification) {
            if (!updatedProfile.parentalControl) {
              updatedProfile.parentalControl = {}
            }
            updatedProfile.parentalControl.contentClassification = contentClassificationLocal
          }

          // Only update if value exists and is different
          if (profilePINLocal && profilePINLocal !== currentPin) {
            if (!updatedProfile.parentalControl) {
              updatedProfile.parentalControl = {}
            }
            updatedProfile.parentalControl.parentalPIN = profilePINLocal
          }
        }

        if (profileId) {
          await VelvetSDK.Storage.Profiles.service.updateProfile(updatedProfile).then(async () => {
            activeProfile &&
              updatedProfile.lang &&
              (await VelvetSDK.Storage.Language.service.setProfilePreferredLanguage(
                userObject?.userID,
                updatedProfile.lang,
                activeProfile?.identifier,
              ))

            // Update activeProfile in localStorage with new language only if it's the same profile being edited
            if (activeProfile && updatedProfile.lang && activeProfile.identifier === profileId) {
              const updatedActiveProfile = {
                ...activeProfile,
                preferredLanguage: updatedProfile.lang,
              }
              localStorage.setItem(LocalStorageItem.ACTIVE_PROFILE, JSON.stringify(updatedActiveProfile))
            }

            updatedProfile.lang && (await fetchVocabulary(updatedProfile.lang))
            await dispatch(fetchMenu(updatedProfile.lang))

            dispatch(setParentalControl(null))
            dispatch(setProfileContentClassification(''))
            dispatch(setProfilePIN(''))
            dispatch(setProfileName(''))
            dispatch(setProfilePhoto(null))
            dispatch(setProfileLang(''))
            navigate(navigateToManageProfiles)
          })
        }
      })
    },
    [
      contentClassificationLocal,
      dispatch,
      isParentalControlEnabled,
      navigate,
      navigateToManageProfiles,
      parentalControlLocal,
      profileData?.image?.backgroundColor,
      profileData?.image?.color,
      profileData?.image?.slug,
      profileData?.image?.type,
      profileData?.image?.url,
      profileData?.name,
      profileData?.parentalControl?.contentClassification,
      profileData?.parentalControl?.parentalPIN,
      profileData?.preferredLanguage,
      profileId,
      profileNameLocal,
      profilePINLocal,
      profilePhotoLocal,
      profileLangLocal,
      fetchVocabulary,
      activeProfile,
      userObject?.userID,
    ],
  )

  useUserProfiles()

  useEffect(() => {
    document.addEventListener('keydown', backToManageProfilePage)
    return () => {
      document.removeEventListener('keydown', backToManageProfilePage)
    }
  }, [backToManageProfilePage])

  useEffect(() => {
    showMenu(false)
    !isLoading && SN.focus('#profileName')
  }, [SN, isLoading, showMenu])

  if (isLoading || isLoadingUpdate) return <DotSpinner />

  return (
    <div className="edit-profile-info-container">
      <div className="profile-details">
        <div className="profile-info">
          <ProfileTitle title={V.profilePreferencesTitle} />
          <ProfileButton
            buttonType={'medium'}
            buttonId={'profileName'}
            buttonText={V.profileNameInfo}
            buttonValue={profileNameLocal || profileData?.name}
            buttonRef={nameRef}
            onKeyDownEvent={(e) => handleNavigation(e, getEditNameProfileIdRoute(profileId))}
            onClick={(e) => handleNavigation(e, getEditNameProfileIdRoute(profileId))}
          />
          <ProfileButton
            buttonType={'medium'}
            buttonId={'icon'}
            buttonText={V.profileIcon}
            onKeyDownEvent={(e) => handleNavigation(e, getEditChooseIconRoute(profileId))}
            onClick={(e) => handleNavigation(e, getEditChooseIconRoute(profileId))}
          />
          <ProfileButton
            buttonType={'medium'}
            buttonId={'profileLanguage'}
            buttonText={V.editProfileInputLanguageLabel}
            buttonValue={getUserLanguageValue(V, profileLangLocal || profileData?.preferredLanguage || language.EN)}
            onKeyDownEvent={(e) => handleNavigation(e, getEditLangProfileIdRoute(profileId))}
            onClick={(e) => handleNavigation(e, getEditLangProfileIdRoute(profileId))}
          />

          <div className="parental-control">
            <Text tag={TextTag.H2}>{V.profileManagementParentalControl}</Text>
          </div>
          <div className="profile-info">
            <ProfileButton
              buttonType={'medium'}
              buttonId={'parental-control'}
              buttonText={V.profileManagementParentalControl}
              buttonValue={parentalControlBtnValue()}
              onKeyDownEvent={handleParentalControl}
              onClick={handleParentalControl}
            />
            <ProfileButton
              buttonType={'medium'}
              buttonId={'content-classification'}
              buttonText={V.profileManagementContentClassification}
              buttonValue={contentClassBtnValue()}
              onKeyDownEvent={handleContentClassification}
              onClick={handleContentClassification}
              disabled={parentalControlLocal === false || (!contentClassificationLocal && !parentalControlStorage)}
            />
            <ProfileButton
              buttonType={'medium'}
              buttonId={'parental-pin'}
              buttonText={V.profileManagementParentalPIN}
              buttonValue={pinBtnValue()}
              onKeyDownEvent={handlePinSetup}
              onClick={handlePinSetup}
              disabled={parentalControlLocal === false || (!profilePINLocal && !parentalControlStorage)}
            />
          </div>
        </div>
        <div className="profile-picture-wrapper">
          <div
            className={classNames('profile-picture', profilePhotoLocal?.type || profileData?.image.type)}
            style={{
              backgroundColor: profilePhotoLocal?.backgroundColor || profileData?.image.backgroundColor,
            }}
          >
            <ProfileIcon
              profileData={profilePhotoLocal || profileData}
              profileName={profileData?.name}
              parentalControl={
                parentalControlLocal === false
                  ? null
                  : contentClassificationLocal || profileData?.parentalControl?.contentClassification
              }
            />
          </div>
          <Text tag={TextTag.P} className="profile-name">
            {profileNameLocal || profileData?.name}
          </Text>
          <div className="buttons">
            <SecondaryButton disabled={!hasChanges()} id="done-btn" onKeyDown={handleDone} onClick={handleDone}>
              {V.genericDoneLabel}
            </SecondaryButton>
            <SecondaryButton onKeyDown={handleCancel} onClick={handleCancel}>
              {V.editProfileCancelButtonLabel}
            </SecondaryButton>
            <SecondaryButton
              id="delete-btn"
              onKeyDown={(e) => handleNavigation(e, getDeleteProfileIdRoute(profileId))}
              onClick={(e) => handleNavigation(e, getDeleteProfileIdRoute(profileId))}
            >
              {V.deleteProfile}
            </SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  )
}
